import React from "react";
import { Tooltip } from "antd";

import ScrapeJobAwareButton from "../../../ScrapeJobs/ScrapeJobAwareButton";

/**
 * VerificationActionButton — the one verification control shared by the Insurance
 * Breakdown and Estimate modals.
 *
 * It used to branch on whether the carrier had a scraper: an instant-AI button
 * for carriers we automate, a manual-request button plus an "urgent" checkbox for
 * the rest, and a "Want to send a Manual Request?" escape hatch between them.
 * That is now one button that opens the Insurance Verification composer, for two
 * reasons:
 *
 *  1. The instant-AI path dispatched an agent WITHOUT opening a request row, so
 *     when it failed there was nothing for `apply_agent_outcome` to hand to FTC
 *     and the failure appeared nowhere. 307 of 348 failed runs on production were
 *     invisible because of it.
 *  2. It asked the office to care whether a carrier is automated. The composer's
 *     own PolicySelectStep says the opposite in as many words — "who services the
 *     request is our problem; surfacing it would only ask the office to make a
 *     choice they have no basis for" — and the server already routes agent vs
 *     manual per policy.
 *
 * Nothing is lost: the composer's endpoint dispatches the agent for carriers that
 * have one, so a scraper carrier still fires immediately.
 */
export default function VerificationActionButton({
  operationType,
  patientId,
  capabilityKnown,
  policySelected,
  hasScraper,
  aiTooltip,
  // Opens the seeded composer. Supplied by the modals via useRequestComposer.
  onOpenComposer,
  requestLabel = "Request Verification",
  // When set (e.g. "Breakdown" / "Estimate" / "Claim Follow-up") the button
  // reads "Request <noun>".
  requestNoun,
  // What we promise for a carrier with no agent. The Claims tab, where nothing
  // is ever automated, says "call the carrier" rather than "pull this".
  manualTooltip = "Our team will pull this verification for you as soon as possible.",
}) {
  const label = requestNoun ? "Request " + requestNoun : requestLabel;

  // Still gated on a chosen policy: the composer needs one to seed, and the
  // capability matrix decides the tooltip's wording below.
  const ready = capabilityKnown && policySelected && typeof onOpenComposer === "function";

  // `hasScraper` no longer changes what the button DOES — only what we promise.
  // Saying "instantly" for a carrier with no agent would be a lie, and saying
  // nothing at all loses the reassurance that the fast path still exists.
  const tooltip = !policySelected
    ? "Choose a policy first."
    : hasScraper
      ? (aiTooltip || "We'll fetch this automatically, usually within a few minutes.")
      : manualTooltip;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <Tooltip title={tooltip}>
        {/* ScrapeJobAwareButton keeps the in-flight state: if an agent is already
            running for this patient the control reflects it, which still matters
            now that the composer can dispatch one. */}
        <ScrapeJobAwareButton
          patientId={patientId}
          operationType={operationType}
          type="primary"
          block
          onClick={function () { if (ready) onOpenComposer(); }}
          disabled={!ready}
        >
          {label}
        </ScrapeJobAwareButton>
      </Tooltip>
    </div>
  );
}
